// src/services/push/FirebaseWebPushProvider.js
//
// FCM Web Push. Firebase is imported DYNAMICALLY so that a build with no
// Firebase configuration never pulls the SDK into the main bundle and never
// throws at module-evaluation time — the CRM must load perfectly on a machine
// where push has not been set up yet.

import WebPushProvider from './WebPushProvider';

// All values are public client config (safe to ship), but they still come from
// the environment so dev/staging/production can point at different projects.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

export default class FirebaseWebPushProvider extends WebPushProvider {
  static providerName = 'fcm';

  constructor() {
    super();
    this._messaging = null;
    this._app = null;
  }

  isConfigured() {
    return !!(firebaseConfig.apiKey && firebaseConfig.projectId
      && firebaseConfig.messagingSenderId && firebaseConfig.appId && VAPID_KEY);
  }

  isSupported() {
    return typeof window !== 'undefined'
      && 'serviceWorker' in navigator
      && 'Notification' in window
      && 'PushManager' in window;
  }

  async _messagingOrNull() {
    if (this._messaging) return this._messaging;
    if (!this.isConfigured() || !this.isSupported()) return null;

    try {
      const [{ initializeApp, getApps }, messagingMod] = await Promise.all([
        import('firebase/app'),
        import('firebase/messaging'),
      ]);
      const { getMessaging, isSupported } = messagingMod;

      // Safari/older browsers can pass the feature checks above yet still not
      // support FCM; this is the SDK's own verdict.
      if (!(await isSupported())) return null;

      this._app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
      this._messaging = getMessaging(this._app);
      return this._messaging;
    } catch (err) {
      console.warn('[push] Firebase messaging unavailable:', err?.message || err);
      return null;
    }
  }

  /** Register the SW that receives pushes while no tab is focused. */
  async _serviceWorker() {
    // The config is passed in the query string because a service worker cannot
    // read the app's environment variables.
    const params = new URLSearchParams({
      apiKey: firebaseConfig.apiKey || '',
      authDomain: firebaseConfig.authDomain || '',
      projectId: firebaseConfig.projectId || '',
      messagingSenderId: firebaseConfig.messagingSenderId || '',
      appId: firebaseConfig.appId || '',
    });
    return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params.toString()}`);
  }

  async requestToken() {
    const messaging = await this._messagingOrNull();
    if (!messaging) return null;

    try {
      // Never prompt twice: a user who already said no is not asked again on
      // every page load.
      if (Notification.permission === 'denied') return null;
      const permission = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
      if (permission !== 'granted') return null;

      const { getToken } = await import('firebase/messaging');
      const registration = await this._serviceWorker();
      return await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
    } catch (err) {
      console.warn('[push] could not obtain a token:', err?.message || err);
      return null;
    }
  }

  async deleteToken() {
    const messaging = await this._messagingOrNull();
    if (!messaging) return false;
    try {
      const { deleteToken } = await import('firebase/messaging');
      return await deleteToken(messaging);
    } catch (err) {
      return false;
    }
  }

  onMessage(cb) {
    let unsub = () => {};
    (async () => {
      const messaging = await this._messagingOrNull();
      if (!messaging) return;
      const { onMessage } = await import('firebase/messaging');
      unsub = onMessage(messaging, (payload) => {
        cb({
          title: payload?.notification?.title || payload?.data?.title,
          body: payload?.notification?.body || payload?.data?.body,
          data: payload?.data || {},
        });
      });
    })();
    return () => { try { unsub(); } catch (e) { /* already torn down */ } };
  }
}
